import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { VehicleChartData } from '../../types/dashboard';

interface Props {
  data: VehicleChartData[];
  loading?: boolean;
}

const formatCurrency = (value: number) => {
  if (value >= 1000000) {
    return (value / 1000000).toFixed(1) + 'M';
  }
  if (value >= 1000) {
    return (value / 1000).toFixed(0) + 'K';
  }
  return value.toString();
};

const formatTooltipValue = (value: number) => {
  return new Intl.NumberFormat('vi-VN').format(value) + 'đ';
};

export const VehicleComparisonChart: React.FC<Props> = ({ data, loading }) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="card p-4">
        <div className="h-6 w-48 bg-gray-200 rounded mb-4 animate-pulse"></div>
        <div className="h-64 bg-gray-100 rounded animate-pulse"></div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="card p-4">
        <h3 className="text-lg font-semibold mb-4">{t('dashboard.vehicleChart')}</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          {t('common.noData')}
        </div>
      </div>
    );
  }

  return (
    <div className="card p-4">
      <h3 className="text-lg font-semibold mb-4">{t('dashboard.vehicleChart')}</h3>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data} layout="vertical" margin={{ left: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            type="number"
            tickFormatter={formatCurrency}
            tick={{ fontSize: 12 }}
            tickLine={false}
            axisLine={{ stroke: '#e5e7eb' }}
          />
          <YAxis
            type="category"
            dataKey="licensePlate"
            tick={{ fontSize: 12 }}
            tickLine={false}
            axisLine={{ stroke: '#e5e7eb' }}
            width={80}
          />
          <Tooltip
            formatter={(value: number) => [formatTooltipValue(value), t('dashboard.profit')]}
            labelFormatter={(label) => `${t('nav.vehicles')}: ${label}`}
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
            }}
            content={({ active, payload, label }) => {
              if (active && payload && payload.length) {
                const item = payload[0].payload as VehicleChartData;
                return (
                  <div className="bg-white p-3 border rounded-lg shadow-sm">
                    <p className="font-semibold">{label}</p>
                    <p className="text-sm">
                      {t('dashboard.profit')}: {formatTooltipValue(item.profit)}
                    </p>
                    <p className="text-sm">
                      {t('dashboard.tripCount')}: {item.tripCount}
                    </p>
                    <p className="text-sm">
                      {t('dashboard.margin')}: {item.profitMargin.toFixed(1)}%
                    </p>
                  </div>
                );
              }
              return null;
            }}
          />
          <Bar dataKey="profit" radius={[0, 4, 4, 0]}>
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.profit >= 0 ? '#22c55e' : '#ef4444'}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
